import React from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import type { AudioRegionReviewConfig, MetricRecord } from "@/metrics/runtime"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { VocalFryConfig } from "../../configuration/components/VocalFryConfig"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

const log = logger("VocalFryMetric")

interface VocalFryResultJson {
  percent_time_in_fry?: number
  fry_time_seconds?: number
  longest_fry_segment_seconds?: number
}

const CATEGORY = "vocal_fry"

export class VocalFryManagerMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "medium" as const
  unit = "s"
  aggregate_units = "s"

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const formatTime = (s: number) =>
        `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`

      const resultJson = parseResultJson<VocalFryResultJson>(mo)
      const segments: SubvalueByTimestamp[] = mo.subvalues_by_timestamp ?? []
      const percentTimeInFry = resultJson?.percent_time_in_fry
      const fryTimeSeconds = resultJson?.fry_time_seconds
      const longestSegment = resultJson?.longest_fry_segment_seconds

      const handleSeekToTimestamp = (timestamp: number) => {
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
          time: timestamp,
        })
      }

      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
              <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                Fry Segments
              </div>
              <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                {segments.length}
              </div>
            </div>

            {fryTimeSeconds != null && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
                <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                  Fry Time
                </div>
                <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                  {fryTimeSeconds.toFixed(2)}s
                </div>
              </div>
            )}

            {percentTimeInFry != null && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
                <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                  Percent Time in Fry
                </div>
                <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                  {percentTimeInFry.toFixed(2)}%
                </div>
              </div>
            )}

            {longestSegment != null && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
                <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                  Longest Segment
                </div>
                <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                  {longestSegment.toFixed(2)}s
                </div>
              </div>
            )}
          </div>

          {segments.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                Vocal Fry Segments
              </h3>
              <div className="space-y-2">
                {segments.map((sv, i) => (
                  <button
                    type="button"
                    key={i}
                    className="w-full text-left flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors cursor-pointer dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-950"
                    onClick={() => handleSeekToTimestamp(sv.start_offset)}
                  >
                    <span className="px-2 py-0.5 rounded text-xs font-semibold border bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800">
                      Vocal Fry
                    </span>
                    <span className="text-sm text-gray-600 font-mono dark:text-gray-400">
                      {formatTime(sv.start_offset)} –{" "}
                      {formatTime(sv.end_offset)}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      ({(sv.end_offset - sv.start_offset).toFixed(2)}s)
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {segments.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No vocal fry detected
            </div>
          )}
        </div>
      )
    } catch (error) {
      log.error(
        "Error rendering vocal fry deep dive",
        error instanceof Error ? error : undefined,
      )
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      // Use top-level subvalues_by_timestamp (preferred source)
      const subvalues: SubvalueByTimestamp[] =
        (mo.subvalues_by_timestamp?.length ?? 0) > 0
          ? (mo.subvalues_by_timestamp ?? [])
          : // @deprecated fallback: result_json.subvalues_by_timestamp — remove after subvalues_by_timestamp migration is complete
            Array.isArray(mo.result_json?.subvalues_by_timestamp)
            ? (mo.result_json.subvalues_by_timestamp as SubvalueByTimestamp[])
            : []
      return subvalues.map((item, idx) => ({
        id: `${CATEGORY}:${idx}`,
        start: item.start_offset,
        end: Math.max(item.end_offset, item.start_offset + 0.5),
        color: "rgba(255, 0, 0, 0.7)",
        drag: false,
        resize: false,
      }))
    } catch (e) {
      log.debug("Vocal fry data unavailable or could not parse", { error: e })
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "seconds",
      buildReviewedResultJson: ({ score, durationSeconds, regions }) => {
        const fryTimeSeconds = Number(score.toFixed(2))
        const longestSegment = regions.reduce(
          (max, r) => Math.max(max, (r.end ?? r.start + 0.05) - r.start),
          0,
        )
        return {
          fry_time_seconds: fryTimeSeconds,
          longest_fry_segment_seconds: Number(longestSegment.toFixed(2)),
          percent_time_in_fry:
            durationSeconds != null && durationSeconds > 0
              ? Number(((fryTimeSeconds / durationSeconds) * 100).toFixed(2))
              : null,
          duration_seconds:
            durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
        }
      },
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
    editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <VocalFryConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(
          metricRecordData,
          onChange,
          textAreaRef,
          editorContext,
        )}
      </div>
    )
  }
}
